"use client";

import { useState } from "react";
import { Check, ShieldCheck, ShoppingCart } from "lucide-react";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import type { ImageFieldValue, LinkTarget } from "../../types";
import { resolveLinkHref } from "../../utils/links";
import { resolveImageValue } from "../../utils/imageOptions";
import { emitAddToCart } from "../../runtime/cartBus";
import { StockBadge } from "../StockBadge";

const FAMILY_LABEL: Record<string, string> = {
  turbo: "Turbo",
  injecteur: "Injecteur",
  "fap-dpf": "FAP / DPF",
  chra: "CHRA",
  geometrie: "Géométrie",
  "kit-joints": "Kit joints",
  "piece-detachee": "Pièce détachée",
};

export type ProductCardItem = {
  title?: string;
  description?: string;
  href?: LinkTarget;
  imageSrc?: ImageFieldValue;
  imageAlt?: string;
  reference?: string;
  brand?: string;
  family?: string;
  power?: string;
  price?: string | number;
  priceOld?: string | number;
  consigne?: string | number;
  warrantyMonths?: string | number;
  stockQuantity?: string | number;
  stockStatus?: string;
  isExchange?: string | boolean;
  /** Optional amber flag ("Best-seller"); shown top-left when there is no discount badge. */
  badge?: string;
};

function euros(v: unknown): number {
  if (v == null) return 0;
  const s = String(v).replace(/\s/g, "").replace(",", ".");
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}
function money(n: number): string {
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " €";
}

/**
 * Dense commerce product card (modeled on the reference parts shops): photo with
 * discount/stock badges, family kicker, 2-line title, mono reference, price row
 * (+ struck old price, consigne note, garantie), and a REAL "Ajouter au panier"
 * button on the card. Root is a <div> (not a link) so the add button and the
 * image/title links coexist as valid HTML.
 */
export function ProductCard({ item }: { item: ProductCardItem }) {
  const { src } = resolveImageValue(item.imageSrc);
  const href = resolveLinkHref(item.href) ?? "/";
  const price = euros(item.price);
  const priceOld = euros(item.priceOld);
  const consigne = euros(item.consigne);
  const discount = priceOld > price && price > 0 ? Math.round(((priceOld - price) / priceOld) * 100) : 0;
  const warranty = item.warrantyMonths ? String(item.warrantyMonths).replace(/[^\d]/g, "") : "";
  const family = item.family ? FAMILY_LABEL[item.family] ?? item.family : "";
  // Record slug = the path's leaf segment (what checkout re-prices against).
  const slug = href.split("/").filter(Boolean).pop() ?? "";
  const out = item.stockStatus === "out-of-stock";
  const [added, setAdded] = useState(false);

  const add = () => {
    if (out || !slug) return;
    emitAddToCart({
      slug,
      title: item.title ?? "",
      price,
      consigne,
      image: typeof item.imageSrc === "string" ? item.imageSrc : "",
      reference: item.reference ?? "",
      quantity: 1,
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="apa-card apa-card-hover group flex h-full flex-col overflow-hidden">
      <RuntimeLink href={href} className="relative block aspect-[4/3] overflow-hidden rounded-t-[var(--radius-card)] bg-[var(--color-surface-2)]">
        {src ? (
          <RuntimeImage src={src} alt={item.imageAlt ?? item.title ?? ""} className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
        ) : null}
        {discount > 0 ? (
          <span className="absolute left-2 top-2 rounded bg-[#B42318] px-1.5 py-0.5 text-[11px] font-bold text-white">-{discount}%</span>
        ) : item.badge ? (
          <span className="absolute left-2 top-2 rounded bg-[#FFC105] px-1.5 py-0.5 text-[11px] font-bold text-[#0A0A0A]">{item.badge}</span>
        ) : null}
        <span className="absolute right-2 top-2">
          <StockBadge quantity={item.stockQuantity} stockStatus={item.stockStatus} />
        </span>
      </RuntimeLink>

      <div className="flex flex-1 flex-col p-3">
        {family ? <span className="text-[10px] font-bold uppercase tracking-widest text-[#00B862]">{family}</span> : null}
        <RuntimeLink href={href} className="mt-0.5 block">
          <h3 className="line-clamp-2 text-sm font-bold leading-snug text-[#121212]">
            <span className="apa-underline-reveal">{item.title}</span>
          </h3>
        </RuntimeLink>

        {(item.reference || item.brand) && (
          <p className="mt-1 truncate text-xs text-[#6B7280]">
            {item.reference && <span className="font-mono font-semibold text-[#121212]">{item.reference}</span>}
            {item.reference && item.brand ? " · " : ""}
            {item.brand}
          </p>
        )}

        <div className="mt-2 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-end gap-1.5">
              <span className="text-lg font-extrabold leading-none text-[#121212]">{money(price)}</span>
              {priceOld > price ? <span className="text-xs text-[#9CA3AF] line-through">{money(priceOld)}</span> : null}
            </div>
            {consigne > 0 ? <p className="mt-0.5 text-[10px] text-[#6B7280]">+ {money(consigne)} consigne</p> : null}
          </div>
          {warranty ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-[#9A6700]">
              <ShieldCheck className="h-3 w-3" /> {warranty} mois
            </span>
          ) : null}
        </div>

        <div className="mt-auto pt-2.5">
          <button
            type="button"
            onClick={add}
            disabled={out}
            className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-[#00B862] text-xs font-bold text-white transition hover:bg-[#00935A] disabled:cursor-not-allowed disabled:bg-[#D1D5DB] disabled:text-[#6B7280] md:text-sm"
          >
            {added ? (<><Check className="h-4 w-4" /> Ajouté</>) : out ? "Indisponible" : (<><ShoppingCart className="h-4 w-4" /> Ajouter au panier</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
